import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { discoverCollectionFields } from "../services/conditionQueryService";
import MatchingPreviewGrid from "../components/MatchingPreviewGrid";
import {
  SafetyWorkflowBanner,
  OperationConfirmModal,
  OperationProgressModal,
  OperationResultModal,
} from "../components/OperationWorkflow";
import { dryRunFindReplace, executeFindReplace } from "../services/findReplaceService";
import { writeAuditLog } from "../services/auditLogService";

export default function FindReplacePage() {
  const { collectionName, selectCollection, allCollections, user, can, Permission } = useFirestoreAdmin();
  const [fields, setFields] = useState([]);
  const [field, setField] = useState("");
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [regex, setRegex] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (!collectionName) return;
    discoverCollectionFields(collectionName).then(setFields).catch(() => setFields([]));
  }, [collectionName]);

  const options = { caseSensitive, regex, wholeWord };

  const runDryRun = async () => {
    if (!can(Permission.edit)) return toast.error("Edit permission required");
    try {
      const report = await dryRunFindReplace(collectionName, field, find, replace, options);
      setPreview(report);
      toast.success(`${report.affectedDocuments} match(es) in sample`);
    } catch (e) {
      toast.error(e?.message || "Dry run failed");
    }
  };

  const execute = async () => {
    setExecuting(true);
    setConfirmOpen(false);
    try {
      const outcome = await executeFindReplace({
        collectionName,
        field,
        find,
        replace,
        options,
        adminUid: user?.uid,
        adminName: user?.email,
        onProgress: setProgress,
      });
      await writeAuditLog({
        adminName: user?.email,
        adminUid: user?.uid,
        action: "find_replace",
        collection: collectionName,
        oldValues: { field, find, replace, options },
        newValues: outcome,
      });
      setResult({ ...outcome, updated: outcome.updated });
      toast.success(`Updated ${outcome.updated}`);
    } catch (e) {
      toast.error(e?.message || "Replace failed");
    } finally {
      setExecuting(false);
      setProgress(null);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Find & Replace</h1>
        <p className="text-sm text-slate-600">Replace text, numbers, booleans, or null across a collection field.</p>
      </header>
      <SafetyWorkflowBanner />
      <div className="fa-card space-y-3 p-4">
        <select className="rounded-lg border px-2 py-1.5 text-sm" value={collectionName} onChange={(e) => selectCollection(e.target.value)}>
          {allCollections.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input list="fr-fields" className="w-full rounded-lg border px-2 py-1.5 text-sm" placeholder="Field" value={field} onChange={(e) => setField(e.target.value)} />
        <datalist id="fr-fields">{fields.map((f) => <option key={f} value={f} />)}</datalist>
        <div className="grid gap-2 sm:grid-cols-2">
          <input className="rounded-lg border px-2 py-1.5 text-sm" placeholder="Find" value={find} onChange={(e) => setFind(e.target.value)} />
          <input className="rounded-lg border px-2 py-1.5 text-sm" placeholder="Replace" value={replace} onChange={(e) => setReplace(e.target.value)} />
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} /> Case sensitive</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={regex} onChange={(e) => setRegex(e.target.checked)} /> Regex</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={wholeWord} onChange={(e) => setWholeWord(e.target.checked)} /> Whole word</label>
        </div>
        <button type="button" className="fa-btn fa-btn-primary" onClick={runDryRun}>Dry run</button>
      </div>
      {preview && (
        <>
          <MatchingPreviewGrid rows={preview.previewRows} showNewValue />
          <button type="button" className="fa-btn fa-btn-danger" onClick={() => setConfirmOpen(true)}>Replace all matches</button>
        </>
      )}
      <OperationConfirmModal open={confirmOpen} title="Find & replace" confirmWord="UPDATE" preview={preview} executing={executing} onCancel={() => setConfirmOpen(false)} onConfirm={execute}>
        <p>Backs up then replaces in batches.</p>
      </OperationConfirmModal>
      <OperationProgressModal progress={progress} />
      <OperationResultModal result={result} labels={{ success: "Updated", failed: "Failed" }} onClose={() => setResult(null)} />
    </div>
  );
}
